import Vue from "vue";
import router from "@/router";
import Wkt from "./wicket";
import { offsetLocation } from "@/js/utils/util.js";

import { wgs84togcj02, gcj02towgs84 } from 'coordtransform'

import {
    area,
    buffer,
    polygon as polygonT,
    booleanValid,
    difference,
    booleanContains,
    bboxPolygon,
    booleanOverlap,
    booleanIntersects,
    intersect
} from '@turf/turf'
import { TDT_TOKEN, MARKER_TYPES, MARKER_BATCH_SIZE, API_STRATEGIES, DASHBOARD_MARKER_TYPES } from "./constants";
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { findPgLandList, findPgOrderList } from '@/api/spatial'
import { L } from "mars2d";


/**
 * 缩放到markerOrPolygon并弹框
 * @param {*} markerOrPolygon marker点对象或者面对象
 * @param {*} map   地图实例
 */
export function zoomPopupMarker(markerOrPolygon, map) {
    //防止marker和map为null，影像flyto报错
    if (!markerOrPolygon || !map) return

    let coord
    if (markerOrPolygon instanceof L.Marker) {
        coord = markerOrPolygon.getLatLng()
        map.flyTo(coord, 17, {
            duration: 1,    //速度1秒
        });
    } else if (markerOrPolygon instanceof L.Polygon) {
        coord = markerOrPolygon.getLatLngs()[0]
        map.fitBounds(coord, {
            padding: [50, 50],
            maxZoom: 17
        });

    }

    if (map.tempLayer.getLayers().length === 0) {
        if (map.markersLayer.getLayers().length !== 0) {
            if (markerOrPolygon instanceof L.Marker) {
                map.markersLayer.zoomToShowLayer(markerOrPolygon, () => {
                    markerOrPolygon.openPopup(); // 确保标记可见后再打开 Popup
                });
            } else if (markerOrPolygon instanceof L.Polygon) {
                markerOrPolygon.openPopup();
            }
        }
    } else {
        markerOrPolygon.openPopup();
    }
}

/**
 * 通过 ID 查找 marker  （orderId/authid等）
 * @param {*} type   数据类型  1-托管服务商,2-农机服务商,3-托管地块,4-农资商.
                     请使用./constants.js
 * @param {*} id  marker的后端传来的id
 * @param {*} tempLayer  储存marker的图层
 * @returns        marker
 */
export function findMarkerById(type, id, tempLayer) {
    let foundMarker = null;
    const strategy = API_STRATEGIES[type];
    tempLayer.eachLayer(marker => {
        let markerId = strategy.getMarkerId(marker.properties);
        if (marker.properties && markerId === id) {
            foundMarker = marker;
        }
    });
    return foundMarker;
}

/**
 * 分页批量加载所有markers
 * @param {*} type   数据类型  1-托管服务商,2-农机服务商,3-托管地块,4-农资商.
 *                   请使用./constants.js
 * @param {*} params 后端接口需要的参数
 * @param {*} map   地图实例
 * @returns
 */
export async function loadMarkersBatch(type, params, map) {
    if (!params || !type) return;

    if (!API_STRATEGIES[type]) {
        console.error(`${type} 错误`);
        return
    }

    try {
        let firstPage, sumCount, totalPage
        const strategy = API_STRATEGIES[type];
        firstPage = await strategy.loadPage(params);
        sumCount = strategy.getTotal(firstPage)
        totalPage = Math.ceil(sumCount / MARKER_BATCH_SIZE);

        const resData = strategy.getData(firstPage)

        // 处理并添加标记到地图
        addMarkers(type, resData, map.tempLayer);

        // 使用setTimeout异步加载剩余页数据
        for (let i = 2; i <= totalPage; i++) {
            setTimeout(async () => {
                params.current = i
                const page = await strategy.loadPage(params);
                const data = strategy.getData(page)
                addMarkers(type, data, map.tempLayer);
            }, (i - 1) * 50); // 每页间隔50ms加载
        }
    } catch (error) {
        console.error("数据获取失败:", error);
    }
}

/**
 * 将地图中所有markers适应全屏范围显示
 * @param {*} map 地图实例
 */
export function fitView(map) {
    if (map.tempLayer.getLayers().length === 0) {
        if (map.markersLayer.getLayers().length === 0) {
            Vue.prototype.$message.warning('当前地图内无任何要素')

        } else {
            const bounds = map.markersLayer.getBounds()
            map.flyToBounds(bounds, {
                padding: [10, 10],
                maxZoom: 17.5,
                duration: 0.3,
            });
        }

    } else {
        const bounds = map.tempLayer.getBounds()
        map.flyToBounds(bounds, {
            padding: [10, 10],
            maxZoom: 17.5,
            duration: 0.3,
        });
    }


}

/**
 * 去除地图中所有markers
 * @param {*} tempLayer 存放markers的图层
 */
export function clearMarkers(tempLayer) {
    tempLayer.eachLayer((marker) => {
        if (marker.off) {
            // 移除所有绑定的事件
            marker.off();
        }
    });
    tempLayer.clearLayers()
}

/**
 * 关闭地图中所有markers的popup
 * @param {*} tempLayer 存放markers的图层
 */
export function closeMarkersPopup(tempLayer) {
    tempLayer.eachLayer((marker) => {
        if (marker) {
            marker.closePopup();
        }
    });
}

/**
 * 将后端返回点数据加入地图 FeatureGroup，并绑定弹出动作和信息
 * @param {*} type   数据类型  1-托管服务商,2-农机服务商,3-托管地块,4-农资商.
 *                   请使用./constants.js
 * @param {*} dataList  后端返回的数据数组
 * @param {*} tempLayer  储存markers的图层
 * @returns
 */
export function addMarkers(type, dataList, tempLayer) {
    console.log("type----", type, "dataList---------------", dataList);
    if (!dataList || !type) return;

    const strategy = API_STRATEGIES[type];

    const coordMap = new Map();
    for (const ele of dataList) {
        if (!ele.centerPoint) {
            if (ele.longitude && ele.latitude) {
                ele.centerPoint = ele.longitude + "," + ele.latitude;
            } else {
                console.log('没有空间信息的marker,跳过', ele)
                continue
            }
        };

        const point = ele.centerPoint.split(",");
        //处理偏移量
        const key = `${point[0]},${point[1]}`;
        const count = coordMap.get(key) || 0;
        coordMap.set(key, count + 1);
        // 计算偏移后的点坐标
        let location = offsetLocation(count, point[0], point[1]);

        //自定义图标
        var customIcon = L.icon({
            iconUrl: strategy.getIcon(), // 图标图像路径
            iconSize: [25, 35], // 图标大小 [宽度, 高度] (像素)
        });
        const [wgs84lng, wgs84lat] = gcj02towgs84(location.lng, location.lat)
        //添加marker到临时图层
        let marker = L.marker([wgs84lat, wgs84lng], {
            icon: customIcon,
        }).addTo(tempLayer);
        marker.properties = ele

        //设置popup弹出框及信息
        let popup = L.popup({ maxWidth: 800 }).setContent("加载中...");
        marker.bindPopup(popup);

        let ComponentClass = Vue.extend(strategy.getInfoComponent())
        const instance = new ComponentClass({
            router,
            propsData: {
                info: ele,
            },
        });
        const content = instance.$mount().$el;

        marker.on("popupopen", async () => {
            // 返回一个 Promise，Leaflet 会自动处理异步内容
            try {
                //模拟后端api异步调用
                setTimeout(() => {
                  popup.setContent(content)
                  // 触发组件内部的加载逻辑
                  if (instance && typeof instance.handlePopupOpen === 'function') {
                    instance.handlePopupOpen()
                  }
                }, 200);
            } catch (error) {
                return `加载失败: ${error.message}`;
            }
        });
    }
}

/**
 * 添加面要素到地图
 * @param {*} tempLayer  图层
 * @param {*} pgAssetList 所有pg库中资源资产或托管单地块的数据
 * @param {*} infoComponent  展示组件
 * @param {*} curTab cqlx标签
 */
export function addPgAssetPolygons(tempLayer, pgAssetList, infoComponent, curTab) {
    for (const pgAsset of pgAssetList) {
        let shapeWgs84 = transGcj02ToWgs84LatlonPolygon(pgAsset.shape)
        //添加polygon到临时图层
        let polygon = L.polygon(shapeWgs84, {
            weight: 1, // 边框粗细
            opacity: 0.8, // 边框透明度（0-1）
            fillOpacity: 0.4,
        }).addTo(tempLayer);
        polygon.properties = pgAsset

        //设置popup弹出框及信息
        let popup = L.popup({ maxWidth: 800 }).setContent("加载中...");
        polygon.bindPopup(popup);

        let ComponentClass = Vue.extend(infoComponent)
        const instance = new ComponentClass({
            router,
            propsData: {
                info: pgAsset,
                curTab: curTab,
            },
        });
        const content = instance.$mount().$el;

        polygon.on("popupopen", async () => {
            try {
                setTimeout(() => {
                  popup.setContent(content)
                  // 触发组件内部的加载逻辑
                  if (instance && typeof instance.handlePopupOpen === 'function') {
                    instance.handlePopupOpen()
                  }
                }, 200);
            } catch (error) {
                return `加载失败: ${error.message}`;
            }
        });
    }
}

/**
 * 根据传入的xmxx集合 获取pg库中所有存在于xmbh集合中的 面要素对象（id,xmbh,shape等）
 * @param {*} dataList 
 * @returns 
 */
export async function loadLandPolygons(dataList, group) {
    console.log(dataList, 'dataList---')
    let res = await findPgLandList()
    if (!res.success) return
    let arr = []
    res.data.forEach(obj => {
        //只要面数据
        if (obj.shape[0].length >= 3) {
            // obj.xmbh 在数组dataList的xmbh集合中存在，则生成面
            let data = dataList.find(item => item.xmbh === obj.xmbh)
            if (data) {
                let marker = findMarkerByXmbh(group, obj.xmbh)
                if (marker) group.removeLayer(marker)
                arr.push({ ...data, ...obj })
            }
        }
    })
    return arr
}

/**
 * 根据传入的order集合 获取pg库中所有存在于orderId集合中的 面要素对象（id,orderId,xmbh,shape等）
 * @param {*} dataList 
 * @returns 
 */
export async function loadOrderPolygons(dataList, group) {
    console.log(dataList, 'dataList---')
    let res = await findPgOrderList()
    if (!res.success) return
    let arr = []
    res.data.forEach(obj => {
        //只要面数据
        if (obj.shape[0].length >= 3) {
            // obj.orderId 在数组dataList的orderId集合中存在，则生成面
            let data = dataList.find(item => item.orderId === obj.orderId)
            if (data) {
                let marker = findMarkerByOrderId(group, obj.orderId)
                if (marker) group.removeLayer(marker)
                arr.push({ ...data, ...obj })
            }
        }
    })
    return arr
}


/**
 * 根据xmbh查询图层中的marker（用于删除地块对应的重复marker,此时在加入面地块之前，xmbh唯一对应点要素）
 * @param {*} group 
 * @param {*} xmbh 
 */
export function findMarkerByXmbh(group, xmbh) {
    for (const layer of group.getLayers()) {
        if (layer instanceof L.Marker) {
            if (layer?.properties?.xmbh === xmbh) {
                return layer;
            }
        }
    }
    return null;
}

/**
 * 根据orderId查询图层中的marker（用于删除 托管单绘制地块 对应的重复marker,此时在加入面地块之前，orderId唯一对应点要素）
 * @param {*} group 
 * @param {*} orderId 
 */
export function findMarkerByOrderId(group, orderId) {
    for (const layer of group.getLayers()) {
        if (layer instanceof L.Marker) {
            if (layer?.properties?.orderId === orderId) {
                return layer;
            }
        }
    }
    return null;
}


/**
 * 将传入的面要素  全部从纬经度的火星坐标系转成wgs84，供leaflet添加面
 * @param {*} polygons 
 * @returns 
 */
export function transGcj02ToWgs84LatlonPolygon(polygon) {
    return polygon.map(ring => {
        return ring.map(point => {
            const [lat, lng] = point;
            const transformed = gcj02towgs84(lng, lat);
            // 转换后再转回 [lat, lng] 格式
            return [transformed[1], transformed[0]];
        });
    });
}

/**
 * 初始化地图  leaflet
 * @param {*} mapId 地图所在的div的id
 * @param {*} options 地图参数
 * @returns
 */
export function initMap(mapId, options = {}) {

    let map = L.map(mapId, {
        renderer: L.canvas(),
        attributionControl: false,
        zoomControl: false,
        center: [31.1989633, 107.5078704],  //达州
        zoom: 10,
        maxZoom: 18,
        minZoom: 4,
        ...options
    });
    //设置背景透明
    map.getContainer().style.backgroundColor = "transparent";

    let tdtImgTileLayer = L.tileLayer(
        'https://t{s}.tianditu.gov.cn/img_w/wmts?layer=img&style=default&tilematrixset=w&Service=WMTS&Request=GetTile&Version=1.0.0&Format=tiles&TileMatrix={z}&TileCol={x}&TileRow={y}&tk=' + TDT_TOKEN,
        {
            subdomains: '01234567',
            attribution: '天地图影像底图'
        }
    ).addTo(map)
    let tdtCiaTileLayer = L.tileLayer(
        'https://t{s}.tianditu.gov.cn/cia_w/wmts?layer=cia&style=default&tilematrixset=w&Service=WMTS&Request=GetTile&Version=1.0.0&Format=tiles&TileMatrix={z}&TileCol={x}&TileRow={y}&tk=' + TDT_TOKEN,
        {
            subdomains: '01234567',
            attribution: '天地图影像注记'
        }
    ).addTo(map)

    //临时图层,用于存放各种地图要素
    map.tempLayer = L.featureGroup().addTo(map);

    // 初始化 MarkerClusterGroup
    map.markersLayer = new L.MarkerClusterGroup({
        maxClusterRadius: 120, // 聚合半径
        disableClusteringAtZoom: 16, // 在高缩放级别禁用聚合
        chunkedLoading: true, // 分块加载，防止卡顿
        spiderfyOnMaxZoom: false, // 点击聚合后是否展开
        polygonOptions: {
            color: '#ff0000',
        }
    }).addTo(map)

    return map;
}

/**
 * 判断数组的维度
 * @param arr 要检测的数组
 * @returns 数组的维度（0表示非数组，1表示一维数组，2表示二维数组，依此类推）
 */
export function getDimension(arr) {
    // 如果不是数组或者是空数组，返回0
    if (!Array.isArray(arr) || arr.length === 0) {
        return 0;
    }

    // 检查数组中的第一个元素是否是数组
    const firstElement = arr[0];
    if (Array.isArray(firstElement)) {
        // 递归检查子数组的维度
        return 1 + getDimension(firstElement);
    } else {
        // 如果第一个元素不是数组，则当前数组是一维数组
        return 1;
    }
}

/**
 * 统一经纬度格式
 * @param {*} polygonCoords 面要素的二维数组格式
 */
export function transToLnglat(polygonCoords) {


    proj4.defs(
        "EPSG:4515",
        "+proj=tmerc +lat_0=0 +lon_0=81 +k=1 +x_0=27500000 +y_0=0 +ellps=GRS80 +units=m +no_defs +type=crs"
    );
    proj4.defs(
        "EPSG:4536",
        "+proj=tmerc +lat_0=0 +lon_0=81 +k=1 +x_0=500000 +y_0=0 +ellps=GRS80 +units=m +no_defs +type=crs"
    );

    let lng = polygonCoords[0][0]
    const [integerStr] = lng.toString().split('.');

    // 取绝对值（处理负数）
    const absIntegerStr = integerStr.replace('-', '');

    if (absIntegerStr.length === 7) {
        polygonCoords = polygonCoords.map(coord => [coord[1], coord[0]])
    }

    //平面坐标
    if (absIntegerStr.length >= 4) {
        polygonCoords = polygonCoords.map((J) => proj4("EPSG:4515", "EPSG:4326", J))
    }

    if (polygonCoords.length > 0 &&
        (polygonCoords[0][0] !== polygonCoords[polygonCoords.length - 1][0] ||
            polygonCoords[0][1] !== polygonCoords[polygonCoords.length - 1][1])) {
        polygonCoords.push(polygonCoords[0])
    }

    return polygonCoords
}

/**
* 统一平面坐标格式
* @param {*} polygonCoords 面要素的二维数组格式
*/
export function transTo4515(polygonCoords) {
    proj4.defs(
        "EPSG:4515",
        "+proj=tmerc +lat_0=0 +lon_0=81 +k=1 +x_0=27500000 +y_0=0 +ellps=GRS80 +units=m +no_defs +type=crs"
    );
    proj4.defs(
        "EPSG:4536",
        "+proj=tmerc +lat_0=0 +lon_0=81 +k=1 +x_0=500000 +y_0=0 +ellps=GRS80 +units=m +no_defs +type=crs"
    );

    let lng = polygonCoords[0][0]
    const [integerStr] = lng.toString().split('.');

    // 取绝对值（处理负数）
    const absIntegerStr = integerStr.replace('-', '');
    //平面坐标
    if (absIntegerStr.length <= 3) {
        polygonCoords = polygonCoords.map((J) => proj4("EPSG:4326", "EPSG:4515", J))
    }
    return polygonCoords
}




/**
 * 创建要素缓冲区
 * @param {GeoJSON} coords - 输入要素(面)的经纬度 二维数组
 * @param {number} radius - 缓冲半径(米)
 * @param {Object} options - 缓冲选项
 * @returns buffered 缓冲区多边形经纬度 二维数组
 */
export function createBuffer(coords, radius, options = {}) {

    const defaultOptions = {
        units: 'meters',  // 单位: meters/kilometers/degrees...
        steps: 64         // 缓冲区的平滑度(边数)
    };

    if (coords.length > 0 &&
        (coords[0][0] !== coords[coords.length - 1][0] ||
            coords[0][1] !== coords[coords.length - 1][1])) {
        coords.push(coords[0])
    }
    const poly = polygonT([coords]);
    let buffered = buffer(poly, radius, { ...defaultOptions, ...options }).geometry.coordinates
    return buffered[0];
}


/**
 *
 * @param {解析excel} file
 */
export function parseExcel(file, callback) {

    const reader = new FileReader();

    reader.onload = (e) => {
        try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            // 获取第一个工作表
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // 转换为 JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length > 0) {
                callback(jsonData)
            } else {
                console.log('Excel 文件中没有数据')
            }
        } catch (err) {
            console.log('解析 Excel 文件失败: ' + err.message)
        }
    };

    reader.onerror = () => {
        console.log('读取文件失败')
    };

    reader.readAsArrayBuffer(file);
}


/**
 * 将Leaflet面坐标转换为GeoJSON兼容的坐标数组
 * @param {L.LatLng[]|L.LatLng[][]} polygonLatlngs - Leaflet面坐标,是二纬数组，里面是对象  [[obj,{lat,lng},obj,obj]]
 * @returns {Array<Array<[number, number]>>} 二维经纬度数组
 */
export function polygonToArray(polygonLatlngs) {
    console.log('polygonLatlngs', polygonLatlngs)
    return polygonLatlngs[0].map(latlng => [latlng.lng, latlng.lat]);
}

/**
 * 经纬度转纬经度  polygon的
 */
export function polygonCoordsToLatlngCoords(coords) {
    return coords.map(coord => [coord[1], coord[0]]);
}


/**
 * 将 经纬度数组形式 转成 后端shape字符串形式
 * @param mode  转成什么类型 （point,line,polygon）
 * @param coords  经纬度数组 点-[] 线-[[],[]] 面-[[],[],[],[]]（注意首尾点闭合）
 *
 */
export function toShape(mode, coords) {
    const wkt = new Wkt.Wkt()
    let shape
    if (mode == 'Marker') {
        wkt.fromObject({
            type: 'Point',
            coordinates: coords
        })
        shape = wkt.write()
    } else if (mode == 'Line') {
        wkt.fromObject({
            type: 'LineString',
            coordinates: coords
        })
        shape = wkt.write()
    } else if (mode == 'Polygon') {
        shape = this.normalizePolygonString(coords)
    }
    return shape
}
function normalizePolygonString(coordinates) {
    if (!Array.isArray(coordinates)) {
        throw new Error('Coordinates must be an array')
    }
    if (coordinates.length < 3) {
        throw new Error('Polygon requires at least 3 vertices')
    }

    let lng = coordinates[0][0]
    const [integerStr] = lng.toString().split('.');
    // 取绝对值（处理负数）
    const absIntegerStr = integerStr.replace('-', '');
    if (absIntegerStr.length === 7) {
        coordinates = coordinates.map(coord => [coord[1], coord[0]])
    }

    const firstPoint = coordinates[0]
    const lastPoint = coordinates[coordinates.length - 1]
    const closedCoordinates = [...coordinates]
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        closedCoordinates.push(firstPoint) // 添加闭合点
    }

    const coordString = closedCoordinates
        .map(coord => `${coord[0]} ${coord[1]}`)
        .join(',')

    return `MULTIPOLYGON(((${coordString})))`
}


/**
* 从字符串shape转成面要素经纬度
* @param {*} shape  字符串shape multipolygon
* @returns  polygonCoords   面要素经纬度 二维数组
*/
export function fromShape(shape) {
    const wkt = new Wkt.Wkt();
    wkt.read(shape);
    const geoJson = wkt.toJson();

    let polygonCoords = geoJson.coordinates[0][0].map((coord) => {
        const [lng, lat] = proj4("EPSG:4515", "EPSG:4326", [
            coord[0],
            coord[1],
        ]);
        return [lat, lng];
    });

    return polygonCoords
}
